import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes, useParams } from "react-router";
import { useWindowSize } from "../../../../../hooks/useWindowSize";
import WS from "../../../../../service/ws";
import API from "../../../../../service/api";
import { TabsViewer } from "../../../../../utils/TabsViewer";
import { MachineViewerPage } from "./machines/[id]";
import { useTranslation } from "react-i18next";
import { MachinesList } from "./MachinesList";
import { objUpdate } from "../../../../../service/utils";
import { LogViewer } from "../../../../../components/LogViewer";
import { OperatorNavigationBar } from "../../../../../components/OperatorNavigationBar";
import { MDBIcon } from "mdb-react-ui-kit";
import { LoadingSpinner } from "../../../../../components/LoadingSpinner";
import { ErrorScreen, ErrorScreenProps } from "../../../../../components/errors/ErrorScreen";
import OpLocationListItemDTO from "@common/dtos/OpLocationListItemDTO";
import OpLocationDTO from "@common/dtos/OpLocationDTO";
import ReportSelectorModal from "../../../../../components/modals/ReportSelectorModal";

export interface LocationViewerPageProps {
    groupId: number;
    groupName: string;
    locations: OpLocationListItemDTO[];
}

export const LocationViewerPage = ({ groupId, groupName, locations }: LocationViewerPageProps) => {
    const params = useParams();
    const { innerWidth } = useWindowSize();
    const { t } = useTranslation();

    const [fetching, setFetching] = useState(false);
    const [needsToFetch, setNeedsToFetch] = useState(true);
    const [location, setLocation] = useState<OpLocationDTO|null>(null);
    const [error, setError] = useState<ErrorScreenProps|null>(null);
    const [reportSelectorOpened, setReportSelectorOpened] = useState(false);

    const id = useMemo(() => Number(params.id), [params]);
    const locationName = useMemo(() => location?.Name || locations.find(l => l.id == id)?.Name || "?", [location, locations, id]);

    const updateLocation = useCallback((updates: any) => {
        setLocation(prev => !prev ? prev : objUpdate(prev, updates));
    }, []);

    useEffect(() => {
        if (!groupId || !id) return;

        WS.emit("location", {groupId: groupId, locationId: id});

        const unlisten = WS.listen("location", ({groupId, locationId, updates}: {groupId: number, locationId: number, updates: any}) => {
            if (groupId !== groupId || locationId !== id) return;
            setLocation(prev => objUpdate(prev, updates));
        });

        return () => {
            unlisten();
            WS.emit("location", {});
        }
        // const unlisten = WS.dataListener({
        //     groupId,
        //     locationId: id,
        //     onUpdate: ({updates}: any) => updateLocation(updates)
        // });
        // return () => unlisten();
    }, [groupId, id]);

    const fetch = useCallback(() => {
        if (!id) return;
        setFetching(true);
        setNeedsToFetch(false);
        setLocation(null);
        API.GET(`/api/groups/${groupId}/${id}`)
            .then(setLocation)
            .catch(err => {
                setError({
                    title: err.code == 404 ? t('errors:not_found') : t('errors:an_issue_occured'),
                    description: err.code == 404
                        ? t('errors:location_not_found')
                        : t('errors:location_issue'),
                    tryAgain: err.code == 404 ? undefined : () => setNeedsToFetch(true)
                })
            })
            .finally(() => setFetching(false));
    }, [groupId, id]);

    useEffect(() => {
        if (needsToFetch && !fetching) fetch();
    }, [needsToFetch, fetching, id]);

    const ReportPopupSection = useMemo(() =>
        location?.value !== undefined
        ?   <span role={location?.allowReport ? "button" : "none"} className="badge badge-success align-items-center" onClick={() => setReportSelectorOpened(location?.allowReport ?? false)}>
                <MDBIcon icon="hand-holding-dollar" size="lg" fixed color="primary" className="me-1"/>
                {location.value}
            </span>
        :   null
    , [groupId, id, location, innerWidth]);

    const MachinesListSection = useMemo(() => (
        <MachinesList machines={location?.machines || []}/>
    ), [location]);

    const LogViewerSection = useMemo(() => <LogViewer groupId={groupId} locationId={id}/>, [groupId, id]);

    return (
        <Routes>
            <Route path="" element={
                <div className="d-flex flex-column align-items-center w-100 h-100" style={{}}>
                    <ReportSelectorModal
                        open={reportSelectorOpened}
                        onClose={() => setReportSelectorOpened(false)}
                        groupId={groupId}
                        locationId={location?.id ?? -1}
                    />
                    <OperatorNavigationBar
                        backTo=".."
                        right={ ReportPopupSection }
                        links={[
                            { name: groupName, to: `/group/${groupId}`, icon: 'layer-group' },
                            { name: locationName, icon: 'map-marker-alt' }
                        ]}
                    />
                    
                    {fetching && <LoadingSpinner/>}
                    {error && <div className="flex-fill my-5"><ErrorScreen {...error}/></div>}

                    {(location && !fetching) &&
                        <div className="d-flex flex-column flex-fill justify-content-center align-items-center w-100 pt-1" style={{overflowX: 'hidden'}}>
                            {innerWidth < 700 &&
                                <TabsViewer
                                    id={'location'}
                                    defaultTab="machines"
                                    tabs={[
                                        {
                                            name: 'machines', title: t('machines'), icon: 'fas fa-server',
                                            component: MachinesListSection
                                        },
                                        {
                                            name: 'logs', title: t('logs'), icon: 'fas fa-bell',
                                            component: LogViewerSection
                                        }
                                    ]}
                                />
                            }
                            {innerWidth >= 700 && (
                                <div className="d-flex flex-row w-100 flex-fill overflow-hidden">
                                    <div className="d-flex flex-column align-items-center col-8 overflow-hidden flex-fill">
                                        { MachinesListSection }
                                    </div>
                                    <div className="d-flex flex-column flex-fill col-4">
                                        <div className="d-flex flex-column flex-fill overflow-auto pe-2">
                                            { LogViewerSection }
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                        </div>
                    }
        
                    
                </div>
            }/>

            {location && <>
                <Route path="machines/:id/*" element={<MachineViewerPage groupId={groupId} groupName={groupName} locationId={id} locationName={location.Name} machines={location.machines ?? []} standalone={location.machines?.length === 1}/>}/> 
                <Route path="next" element={(location.machines && location.machines?.length > 0) ? <Navigate to={"../machines/"+location.machines[0].id}/> : <Navigate to=""/>}/>
                <Route path="*" element={<Navigate to=".."/>}/>
            </>}
            
            
        </Routes>
        
    )
}